import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../api/client';

interface SliderImage { id: number; imageS3: string | null; }
const SLIDE_INTERVAL_MS = 5000;
const FALLBACK_SLIDES: SliderImage[] = [{ id: 1, imageS3: null }, { id: 2, imageS3: null }, { id: 3, imageS3: null }];

export default function Slider() {
  const { t } = useTranslation();
  const [slides, setSlides] = useState<SliderImage[]>([]);
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE}/slider`)
      .then(res => setSlides(res.data.length > 0 ? res.data : FALLBACK_SLIDES))
      .catch(() => setSlides(FALLBACK_SLIDES));
  }, []);

  const next = useCallback(() => setCurrent(p => (p + 1) % (slides.length || 1)), [slides.length]);
  const prev = useCallback(() => setCurrent(p => (p - 1 + slides.length) % (slides.length || 1)), [slides.length]);

  useEffect(() => {
    if (slides.length === 0 || paused) return;
    const t = setInterval(next, SLIDE_INTERVAL_MS);
    return () => clearInterval(t);
  }, [slides.length, next, paused]);

  if (slides.length === 0) return null;

  return (
    <section style={{ position: 'relative', height: 'clamp(400px, 60vh, 600px)', overflow: 'hidden', background: '#0c1a3a' }}
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      {slides.map((slide, i) => (
        <div key={slide.id} style={{ position: 'absolute', inset: 0, opacity: i === current ? 1 : 0, transition: 'opacity 0.9s ease', zIndex: i === current ? 1 : 0 }}>
          {slide.imageS3 ? (
            <img src={`${API_BASE}/slider/images/${slide.imageS3}`} alt={`${t('slider_slide')} ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }} />
          ) : (
            <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #0c1a3a 0%, #1e40af 50%, #0891b2 100%)' }} />
          )}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.3) 60%, transparent 100%)' }} />
        </div>
      ))}

      <div style={{ position: 'absolute', inset: 0, zIndex: 2, display: 'flex', alignItems: 'center', maxWidth: 1280, margin: '0 auto', padding: '0 clamp(20px, 5vw, 64px)', left: 0, right: 0 }}>
        <div style={{ maxWidth: 'min(600px, 90%)' }}>
          <div style={{ display: 'inline-block', background: 'rgba(16,185,129,0.9)', color: '#fff', fontSize: 'clamp(0.65rem, 1.5vw, 0.78rem)', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', padding: '5px 14px', borderRadius: 20, marginBottom: 'clamp(12px, 3vw, 20px)' }}>
            {t('slider_badge')}
          </div>
          <h1 style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 'clamp(1.5rem, 5vw, 3rem)', fontWeight: 800, color: '#fff', lineHeight: 1.2, marginBottom: 'clamp(12px, 3vw, 20px)', textShadow: '0 2px 12px rgba(0,0,0,0.3)' }}>
            {t('slider_title')}
          </h1>
          <p style={{ fontSize: 'clamp(0.85rem, 2vw, 1.05rem)', color: 'rgba(255,255,255,0.85)', marginBottom: 'clamp(20px, 4vw, 36px)', lineHeight: 1.6 }}>
            {t('slider_desc')}
          </p>
          <div style={{ display: 'flex', gap: 'clamp(8px, 2vw, 14px)', flexWrap: 'wrap' }}>
            <a href="/join" style={{ background: '#10b981', color: '#fff', padding: 'clamp(10px, 2vw, 14px) clamp(18px, 4vw, 32px)', borderRadius: 10, fontWeight: 700, fontSize: 'clamp(0.8rem, 2vw, 0.95rem)', textDecoration: 'none', display: 'inline-block' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#059669'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#10b981'; }}>
              {t('slider_join')}
            </a>
            <a href="/contacts" style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', color: '#fff', padding: 'clamp(10px, 2vw, 14px) clamp(18px, 4vw, 32px)', borderRadius: 10, fontWeight: 600, fontSize: 'clamp(0.8rem, 2vw, 0.95rem)', textDecoration: 'none', border: '1px solid rgba(255,255,255,0.3)' }}>
              {t('slider_contact')}
            </a>
          </div>
        </div>
      </div>

      {slides.length > 1 && (
        <>
          <button onClick={prev} style={arrowStyle('left')}>‹</button>
          <button onClick={next} style={arrowStyle('right')}>›</button>
        </>
      )}

      <div style={{ position: 'absolute', bottom: 'clamp(16px, 4vw, 28px)', left: '50%', transform: 'translateX(-50%)', zIndex: 3, display: 'flex', gap: 8 }}>
        {slides.map((_, i) => (
          <button key={i} onClick={() => setCurrent(i)} style={{ width: i === current ? 28 : 8, height: 8, borderRadius: 4, border: 'none', cursor: 'pointer', background: i === current ? '#10b981' : 'rgba(255,255,255,0.4)', transition: 'all 0.3s', padding: 0 }} />
        ))}
      </div>

      {!paused && (
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, background: 'rgba(255,255,255,0.15)', zIndex: 3 }}>
          <div key={current} style={{ height: '100%', background: '#10b981', animation: `progress ${SLIDE_INTERVAL_MS}ms linear forwards` }} />
        </div>
      )}
      <style>{`@keyframes progress { from{width:0%} to{width:100%} }`}</style>
    </section>
  );
}

function arrowStyle(side: 'left' | 'right'): React.CSSProperties {
  return { position: 'absolute', top: '50%', transform: 'translateY(-50%)', [side]: 'clamp(10px, 2vw, 20px)', zIndex: 3, background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', fontSize: 'clamp(1.4rem, 3vw, 1.8rem)', width: 'clamp(40px, 8vw, 48px)', height: 'clamp(40px, 8vw, 48px)', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
}
